import React, { useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Rnd } from 'react-rnd';
import { Button } from 'react-bootstrap';
import { CheckerContent } from './CheckerContent';
import { loadCheckerPanelState, saveCheckerPanelState } from '../utils/checkerPanelStorage';

const MARGIN = 20;

function clampPosition(x: number, y: number, width: number, height: number) {
  const maxX = typeof window !== 'undefined' ? window.innerWidth - width - MARGIN : 800;
  const maxY = typeof window !== 'undefined' ? window.innerHeight - height - MARGIN : 600;
  return {
    x: Math.max(MARGIN, Math.min(maxX, x)),
    y: Math.max(MARGIN, Math.min(maxY, y)),
  };
}

interface CheckerModalProps {
  show: boolean;
  onHide: () => void;
  initialSku?: string;
  initialSearchAsSubject?: boolean;
}

const CheckerModal: React.FC<CheckerModalProps> = ({ show, onHide, initialSku = '', initialSearchAsSubject = false }) => {
  const saved = useMemo(() => loadCheckerPanelState(), [show]);

  const defaultPosition = useMemo(() => {
    const w = saved.width || 600;
    const h = saved.height || 400;
    const winW = typeof window !== 'undefined' ? window.innerWidth : 1200;
    const winH = typeof window !== 'undefined' ? window.innerHeight : 800;
    const x = saved.x ?? (winW - w) / 2;
    const y = saved.y ?? (winH - h) / 2;
    return clampPosition(x, y, w, h);
  }, [saved.x, saved.y, saved.width, saved.height]);

  const handleDragStop = useCallback((_e: any, d: { x: number; y: number }) => {
    saveCheckerPanelState({ x: d.x, y: d.y });
  }, []);

  const handleResizeStop = useCallback((_e: any, _dir: any, ref: HTMLElement, _delta: any, pos: { x: number; y: number }) => {
    const clamped = clampPosition(pos.x, pos.y, ref.offsetWidth, ref.offsetHeight);
    saveCheckerPanelState({
      x: clamped.x,
      y: clamped.y,
      width: ref.offsetWidth,
      height: ref.offsetHeight,
    });
  }, []);

  if (!show) return null;

  const content = (
    <Rnd
      default={{
        x: defaultPosition.x,
        y: defaultPosition.y,
        width: saved.width,
        height: saved.height,
      }}
      bounds="window"
      minWidth={400}
      minHeight={300}
      maxWidth={typeof window !== 'undefined' ? window.innerWidth - 40 : 1400}
      maxHeight={typeof window !== 'undefined' ? window.innerHeight - 40 : 900}
      onDragStop={handleDragStop}
      onResizeStop={handleResizeStop}
      dragHandleClassName="checker-panel-drag-handle"
      style={{
        position: 'fixed',
        zIndex: 1000,
        pointerEvents: 'auto',
        display: 'flex',
        flexDirection: 'column',
        background: '#212529',
        borderRadius: 8,
        boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
        overflow: 'hidden',
        border: '1px solid #495057',
      }}
    >
      <div
        className="checker-panel-drag-handle"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '8px 12px',
          background: '#343a40',
          borderBottom: '1px solid #495057',
          cursor: 'move',
        }}
      >
        <span style={{ fontWeight: 600, color: '#fff' }}>Чекер</span>
        <Button variant="link" size="sm" onClick={(e) => { e.stopPropagation(); onHide(); }} style={{ color: '#adb5bd', padding: '0 4px' }}>
          ✕
        </Button>
      </div>
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'auto',
          padding: 12,
        }}
      >
        <div style={{ zoom: 0.75 } as React.CSSProperties}>
          <CheckerContent initialSku={initialSku} initialSearchAsSubject={initialSearchAsSubject} />
        </div>
      </div>
    </Rnd>
  );

  const container = typeof document !== 'undefined'
    ? (document.getElementById('checker-modal-root') ?? document.body)
    : null;
  if (!container) return null;
  return createPortal(content, container);
};

export default CheckerModal;
